// Route utilities for handling route endpoints and waypoints
import { supabase } from '../components/data/supabaseClient';

/**
 * Get the correct start and end addresses for a post from route_points table
 * Falls back to original address fields if no route_points exist
 * 
 * @param {Object} post - The post object
 * @param {boolean} isPackage - Whether this is a package or regular post
 * @returns {Object} - Object with corrected address information
 */
export const getCorrectRouteEndpoints = async (post, isPackage = false) => {
  if (!post || !post.id) {
    return post;
  }

  try {
    // Query route_points to get actual start and end points
    // Build filter string to avoid ternary operator issues
    const idColumn = isPackage ? 'package_id' : 'post_id';
    const filterString = `${idColumn}.eq.${post.id},is_start.eq.true,is_end.eq.true`;
    
    const { data: routePoints, error } = await supabase
      .from('route_points')
      .select('id,location,point_order,is_start,is_end,package_id,post_id')
      .eq(idColumn, post.id)
      .or('is_start.eq.true,is_end.eq.true')
      .order('point_order', { ascending: true });

    if (error) {
      console.error('Error fetching route points:', error);
      return post;
    }

    if (!routePoints || routePoints.length === 0) {
      // No route points found, return original post
      return post;
    }

    // Find start and end points
    const startPoint = routePoints.find(point => point.is_start === true);
    const endPoint = routePoints.find(point => point.is_end === true);

    // If we have both start and end points, try to get address info
    if (startPoint && endPoint) {
      // For now, we'll use a simple approach to get city names from coordinates
      // In a production environment, you might want to use a geocoding service
      
      const correctedPost = {
        ...post,
        // Keep original fields for reference
        original_address_from_street: post.address_from_street,
        original_address_from_city: post.address_from_city,
        original_address_to_street: post.address_to_street,
        original_address_to_city: post.address_to_city,
        // Add route point coordinates (PostGIS POINT format is "POINT(lng lat)")
        start_point_lat: startPoint.location ? parseFloat(startPoint.location.match(/POINT\(([^)]+)\)/)?.[1]?.split(' ')[1]) : null,
        start_point_lng: startPoint.location ? parseFloat(startPoint.location.match(/POINT\(([^)]+)\)/)?.[1]?.split(' ')[0]) : null,
        end_point_lat: endPoint.location ? parseFloat(endPoint.location.match(/POINT\(([^)]+)\)/)?.[1]?.split(' ')[1]) : null,
        end_point_lng: endPoint.location ? parseFloat(endPoint.location.match(/POINT\(([^)]+)\)/)?.[1]?.split(' ')[0]) : null,
        has_route_points: true
      };

      // Try to get better address information for end point
      if (endPoint.location) {
        try {
          // Parse coordinates from PostGIS point format
          const coordsMatch = endPoint.location.match(/POINT\(([^)]+)\)/);
          if (coordsMatch) {
            const [lng, lat] = coordsMatch[1].split(' ').map(parseFloat);
            
            // Try to reverse geocode the end point to get actual city name
            const addressInfo = await reverseGeocodeCoordinates(lat, lng);
            if (addressInfo) {
              correctedPost.address_to_street = addressInfo.formatted_address || post.address_to_street;
              correctedPost.address_to_city = addressInfo.city || post.address_to_city;
              correctedPost.address_to_country = addressInfo.country || post.address_to_country;
            }
          }
        } catch (geoError) {
          console.error('Error parsing coordinates:', geoError);
        }
      }

      return correctedPost;
    }

    return post;
  } catch (error) {
    console.error('Error getting correct route endpoints:', error);
    return post;
  }
};

/**
 * Get corrected endpoints for multiple posts/packages
 * 
 * @param {Array} posts - Array of posts/packages
 * @param {boolean} isPackage - Whether these are packages or regular posts
 * @returns {Array} - Array of posts with corrected endpoints
 */
export const getCorrectRouteEndpointsForMultiple = async (posts, isPackage = false) => {
  if (!posts || posts.length === 0) {
    return posts;
  }

  try {
    // Get all post/package IDs
    const ids = posts.map(post => post.id);
    
    // Query all route_points for these posts/packages in one go
    const idColumn = isPackage ? 'package_id' : 'post_id';
    
    const { data: routePoints, error } = await supabase
      .from('route_points')
      .select('id,location,point_order,is_start,is_end,package_id,post_id')
      .in(idColumn, ids)
      .or('is_start.eq.true,is_end.eq.true')
      .order('point_order', { ascending: true });

    if (error) {
      console.error('Error fetching route points:', error);
      return posts;
    }

    if (!routePoints || routePoints.length === 0) {
      return posts;
    }

    // Group route points by post/package ID
    const routePointsByPostId = {};
    routePoints.forEach(point => {
      const postId = isPackage ? point.package_id : point.post_id;
      if (!routePointsByPostId[postId]) {
        routePointsByPostId[postId] = [];
      }
      routePointsByPostId[postId].push(point);
    });

    // Process each post/package
    const correctedPosts = await Promise.all(
      posts.map(async (post) => {
        const postRoutePoints = routePointsByPostId[post.id];
        
        if (!postRoutePoints || postRoutePoints.length === 0) {
          return post;
        }

        const startPoint = postRoutePoints.find(point => point.is_start === true);
        const endPoint = postRoutePoints.find(point => point.is_end === true);

        if (!startPoint || !endPoint) {
          return post;
        }

        // Parse end point coordinates and try to get address
        let correctedEndAddress = null;
        try {
          const coordsMatch = endPoint.location.match(/POINT\(([^)]+)\)/);
          if (coordsMatch) {
            const [lng, lat] = coordsMatch[1].split(' ').map(parseFloat);
            console.log(`Correcting endpoint for post ${post.id}: coordinates ${lat}, ${lng}`);
            correctedEndAddress = await reverseGeocodeCoordinates(lat, lng);
            if (correctedEndAddress) {
              console.log(`✅ Corrected destination for post ${post.id}: ${post.address_to_city} → ${correctedEndAddress.city}`);
            }
          }
        } catch (geoError) {
          console.error('Error processing coordinates for post', post.id, ':', geoError);
        }

        return {
          ...post,
          // Keep original fields for reference
          original_address_to_street: post.address_to_street,
          original_address_to_city: post.address_to_city,
          original_address_to_country: post.address_to_country,
          // Use corrected end address if available
          address_to_street: correctedEndAddress?.formatted_address || post.address_to_street,
          address_to_city: correctedEndAddress?.city || post.address_to_city,
          address_to_country: correctedEndAddress?.country || post.address_to_country,
          has_route_points: true,
          route_corrected: !!correctedEndAddress
        };
      })
    );

    return correctedPosts;
  } catch (error) {
    console.error('Error getting correct route endpoints for multiple posts:', error);
    return posts;
  }
};

/**
 * Enhanced reverse geocoding function with more Latvian cities
 * In production, you might want to use Google Maps API or another service
 * For now, this provides comprehensive city name extraction from coordinates
 * 
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Object|null} - Address information or null
 */
const reverseGeocodeCoordinates = async (lat, lng) => {
  try {
    console.log('Reverse geocoding coordinates:', lat, lng);
    
    // Enhanced coordinate-to-city mapping for Latvia with more cities and better coverage
    const latvianCities = [
      // Major cities
      { name: 'Rīga', lat: 56.9677, lng: 24.1056, radius: 0.15 },
      { name: 'Daugavpils', lat: 55.8754, lng: 26.5351, radius: 0.08 },
      { name: 'Liepāja', lat: 56.5055, lng: 21.0097, radius: 0.08 },
      { name: 'Jelgava', lat: 56.6503, lng: 23.7293, radius: 0.08 },
      { name: 'Jūrmala', lat: 56.9731, lng: 23.7805, radius: 0.08 },
      { name: 'Ventspils', lat: 57.3947, lng: 21.5681, radius: 0.08 },
      
      // Medium cities  
      { name: 'Rēzekne', lat: 56.5077, lng: 27.3331, radius: 0.06 },
      { name: 'Valmiera', lat: 57.5410, lng: 25.4265, radius: 0.06 },
      { name: 'Cēsis', lat: 57.3119, lng: 25.2744, radius: 0.06 },
      { name: 'Ogre', lat: 56.8138, lng: 24.6061, radius: 0.06 },
      { name: 'Salaspils', lat: 56.8614, lng: 24.3501, radius: 0.06 },
      { name: 'Sigulda', lat: 57.1540, lng: 24.8543, radius: 0.06 },
      { name: 'Tukums', lat: 56.9669, lng: 23.1537, radius: 0.06 },
      { name: 'Bauska', lat: 56.4087, lng: 24.1944, radius: 0.06 },
      { name: 'Dobele', lat: 56.6242, lng: 23.2807, radius: 0.06 },
      { name: 'Krāslava', lat: 55.8948, lng: 27.1654, radius: 0.06 },
      { name: 'Limbaži', lat: 57.5129, lng: 24.7153, radius: 0.06 },
      
      // Smaller towns
      { name: 'Saldus', lat: 56.6646, lng: 22.4879, radius: 0.05 },
      { name: 'Kuldīga', lat: 56.9717, lng: 21.9693, radius: 0.05 },
      { name: 'Talsi', lat: 57.2463, lng: 22.5806, radius: 0.05 },
      { name: 'Madona', lat: 56.8547, lng: 26.2176, radius: 0.05 },
      { name: 'Aizkraukle', lat: 56.6044, lng: 25.2551, radius: 0.05 },
      { name: 'Ludza', lat: 56.3464, lng: 27.7261, radius: 0.05 },
      { name: 'Gulbene', lat: 57.1776, lng: 26.7533, radius: 0.05 },
      { name: 'Balvi', lat: 57.1313, lng: 27.2627, radius: 0.05 },
      { name: 'Preiļi', lat: 56.2940, lng: 26.7268, radius: 0.05 },
      { name: 'Alūksne', lat: 57.4222, lng: 27.0427, radius: 0.05 },
      { name: 'Smiltene', lat: 57.4244, lng: 25.9067, radius: 0.05 },
      { name: 'Straupe', lat: 57.3550, lng: 24.9516, radius: 0.05 },
      { name: 'Ozolnieki', lat: 56.6871, lng: 23.7834, radius: 0.05 },
      { name: 'Olaine', lat: 56.7857, lng: 23.9407, radius: 0.05 },
      { name: 'Ķekava', lat: 56.8231, lng: 24.2334, radius: 0.05 },
      { name: 'Mārupe', lat: 56.9119, lng: 23.9777, radius: 0.05 },
      { name: 'Carnikava', lat: 57.1287, lng: 24.2751, radius: 0.05 },
      { name: 'Adaži', lat: 57.0751, lng: 24.3214, radius: 0.05 }
    ];

    // Find the closest city
    let closestCity = null;
    let minDistance = Infinity;

    latvianCities.forEach(city => {
      const distance = Math.sqrt(
        Math.pow(lat - city.lat, 2) + Math.pow(lng - city.lng, 2)
      );
      
      if (distance < city.radius && distance < minDistance) {
        minDistance = distance;
        closestCity = city;
      }
    });

    if (closestCity) {
      console.log('Found closest city:', closestCity.name, 'distance:', minDistance);
      return {
        formatted_address: `${closestCity.name}, Latvia`,
        city: closestCity.name,
        country: 'Latvia'
      };
    }

    // Check if coordinates are in neighboring countries
    if (lng > 28) {
      console.log('Coordinates appear to be in Russia/Belarus');
      return {
        formatted_address: 'Russia/Belarus border area',
        city: 'Border area',
        country: 'Russia/Belarus'
      };
    } else if (lng > 27) {
      console.log('Coordinates appear to be near Lithuanian border');
      return {
        formatted_address: 'Lithuania border area', 
        city: 'Border area',
        country: 'Lithuania'
      };
    } else if (lng < 20.5) {
      console.log('Coordinates appear to be in Estonia/Lithuania');
      return {
        formatted_address: 'Estonia/Lithuania border area',
        city: 'Border area', 
        country: 'Estonia/Lithuania'
      };
    }

    // If no specific city found but within Latvia's general bounds, return generic Latvia location
    console.log('No specific city found, returning generic Latvia');
    return {
      formatted_address: 'Latvia',
      city: 'Latvia',
      country: 'Latvia'
    };
  } catch (error) {
    console.error('Error in reverse geocoding:', error);
    return null;
  }
};

/**
 * Update posts/packages data with correct route endpoints
 * This function can be called to fix existing data in the RecentPosts component
 * 
 * @param {Array} posts - Array of posts/packages
 * @param {boolean} isPackage - Whether these are packages
 * @returns {Array} - Posts with corrected route information
 */
export const fixRouteEndpointsInPosts = async (posts, isPackage = false) => {
  console.log(`Fixing route endpoints for ${posts.length} ${isPackage ? 'packages' : 'posts'}`);
  
  const correctedPosts = await getCorrectRouteEndpointsForMultiple(posts, isPackage);
  
  const correctedCount = correctedPosts.filter(post => post.route_corrected).length;
  console.log(`Successfully corrected ${correctedCount} route endpoints out of ${posts.length} ${isPackage ? 'packages' : 'posts'}`);
  
  return correctedPosts;
};

/**
 * Quick function to get the final destination city from route points
 * This is used for immediate display fixes without full geocoding
 * 
 * @param {string} id - Post or package ID
 * @param {boolean} isPackage - Whether this is a package
 * @returns {string|null} - Final destination city name or null
 */
export const getFinalDestinationFromRoutePoints = async (id, isPackage = false) => {
  try {
    const idColumn = isPackage ? 'package_id' : 'post_id';
    
    const { data: endPoint, error } = await supabase
      .from('route_points')
      .select('location')
      .eq(idColumn, id)
      .eq('is_end', true)
      .single();

    if (error || !endPoint) {
      return null;
    }

    // Parse coordinates and get city name
    const coordsMatch = endPoint.location.match(/POINT\(([^)]+)\)/);
    if (coordsMatch) {
      const [lng, lat] = coordsMatch[1].split(' ').map(parseFloat);
      const addressInfo = await reverseGeocodeCoordinates(lat, lng);
      return addressInfo?.city || null;
    }

    return null;
  } catch (error) {
    console.error('Error getting final destination:', error);
    return null;
  }
};